import React, { useContext, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import type { UseFormReturnType } from '@mantine/form';
import { Stack } from '@mantine/core';
import { IconX } from '@tabler/icons-react';

import dt from '~/testing';
import { Button } from '~/domains/design-system/Button';
import { TextInput } from '~/domains/design-system/TextInput';
import { SignUpHeader } from '~/domains/auth/components/SignUpHeader/SignUpHeader';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { FEATURE } from '~/common/enums/feature.enum';
import { BRANDS } from '~/domains/auth/consts';
import { createEnterKeyHandler } from '~/domains/auth/utils/formHelpers';
import { useValidatePromoOrReferalCode } from '~/domains/promotions/domains/promoCodes/hooks/useValidatePromoOrReferalCode';
import { PromotionsModalsContext } from '~/domains/promotions/Modals/PromotionsModalsContext';
import { reportPromoCodeValidate } from '~/domains/promotions/utils/analytics';
import { PromotionActionLocation } from '~/domains/promotions/constants/analytics-enums';

import { ConnectedRegistrationBonusBanner } from '../../RegistrationBonusBanner/ConnectedRegistrationBonusBanner';
import { EOCallout } from '../../EOCallout/EOCallout';
import { RecaptchaText } from '../../RecaptchaText';
import type { SignUpFormValues } from '../types';
import { RegistrationPromoCodeBanner } from '../../RegistrationPromoCodeBanner';

import SignUpLegal from './SignUpLegal';
import useStyles from './styles';
import { SignUpContinueButton } from './SignUpContinueButton';

interface EmailFormProps {
  form: UseFormReturnType<SignUpFormValues>;
  onClick: () => void;
  brand?: BRANDS;
}

export function EmailForm({ form, onClick, brand = BRANDS.SPLASH }: EmailFormProps) {
  const { t } = useTranslation('auth');
  const { classes, theme } = useStyles();
  const isReferAFriendEnabled = useIsFeatureEnabled(FEATURE.ENABLE_REFER_A_FRIEND_V2);
  const isPromoCodesEnabled = useIsFeatureEnabled(FEATURE.ENABLE_PROMO_CODES);
  const [codeError, setCodeError] = useState<string | undefined>(undefined);

  const isEmailError = !!form.errors.email;
  const isEmailEmpty = !form.values.email?.trim();

  // @ts-expect-error form must share promo & referral code fields. It's kinda hard to type this properly
  const { isValidating, handleValidateCode } = useValidatePromoOrReferalCode({ form });
  const { openModalUnique, closeModalsByType, disableClose, enableClose, isCloseDisabled } =
    useContext(PromotionsModalsContext);

  const isButtonDisabled = isEmailError || isEmailEmpty;

  const handleContinueAction = async () => {
    if (form.values.promoOrReferralCode.trim().length === 0) return onClick();

    // Check the promo/referral code validity
    const {
      codeError: validationError,
      promoCode,
      referralCode,
      bonusModalProps,
    } = await handleValidateCode();

    reportPromoCodeValidate({
      location: PromotionActionLocation.SIGN_UP,
      promoCode: form.values.promoOrReferralCode,
      isValid: !validationError,
    });

    if (validationError) {
      // Intentionally setting the error to an empty string to not show error message under input, but still input's error state
      form.setFieldError('promoOrReferralCode', ' ');
      form.setFieldValue('_promoCodeValue', undefined);
      form.setFieldValue('_referralCodeValue', undefined);
      form.setFieldValue('_promoModalProps', undefined);
      return setCodeError(validationError);
    }

    // Using hidden fields to store the promo/referral code values & promoBonus modal props, so we don't need to use Context
    if (promoCode) {
      form.setFieldValue('_referralCodeValue', undefined);
      form.setFieldValue('_promoCodeValue', promoCode);
    } else if (referralCode) {
      form.setFieldValue('_promoCodeValue', undefined);
      form.setFieldValue('_referralCodeValue', referralCode);
    }
    form.clearFieldError('promoOrReferralCode');
    setCodeError(undefined);

    if (bonusModalProps) {
      form.setFieldValue('_promoModalProps', bonusModalProps);
      disableClose();
      openModalUnique({
        type: 'PromoBonusModal' as const,
        isCloseDisabled,
        ...bonusModalProps,
        onAction: () => {
          closeModalsByType('PromoBonusModal');
          onClick();
          enableClose();
        },
      });
    }
  };

  const handleContinue = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleContinueAction();
  };

  const handleClearPromoOrReferalCode = () => {
    form.setFieldValue('promoOrReferralCode', '');
    form.clearFieldError('promoOrReferralCode');
    setCodeError(undefined);
    form.setFieldValue('_referralCodeValue', undefined);
    form.setFieldValue('_promoCodeValue', undefined);
    form.setFieldValue('_promoModalProps', undefined);
  };

  const handleKeyDown = createEnterKeyHandler(isButtonDisabled, handleContinueAction);

  return (
    <>
      {['RYP', 'OFP'].includes(brand) && (
        <div className={classes.eoCalloutContainer}>
          <EOCallout brand={brand} />
        </div>
      )}
      <div className={classes.headerContainer}>
        <SignUpHeader
          title={t('signUp.headers.email.title')}
          copy={t('signUp.headers.email.copy')}
        />
      </div>
      <div className={classes.formContainer}>
        <TextInput
          className={classes.textInput}
          data-test-id={dt.components.shared.auth.signUp.emailInput}
          errorProps={{
            'data-test-id': dt.components.shared.auth.signUp.emailError,
          }}
          label={t('signUp.email.label')}
          w="100%"
          autoFocus
          onKeyDown={handleKeyDown}
          {...form.getInputProps('email')}
        />

        {(isReferAFriendEnabled || isPromoCodesEnabled) && brand === BRANDS.SPLASH && (
          <Stack spacing={theme.other.spacing._8} w="100%">
            <TextInput
              className={classes.textInput}
              data-test-id={dt.components.shared.auth.signUp.referralCodeInput}
              errorProps={{
                'data-test-id': dt.components.shared.auth.signUp.referralCodeError,
              }}
              label={t('signUp.referralCode.label')}
              w="100%"
              onKeyDown={handleKeyDown}
              rightSection={
                form.values.promoOrReferralCode.length > 0 && (
                  <Button
                    variant="text"
                    size="small-icon"
                    onClick={handleClearPromoOrReferalCode}
                    type="button"
                  >
                    <IconX size={20} color={theme.colors.gray2[4]} />
                  </Button>
                )
              }
              {...form.getInputProps('promoOrReferralCode')}
            />
          </Stack>
        )}
        <SignUpLegal />
      </div>

      <Stack spacing={theme.other.spacing._12} mt={theme.other.spacing._40}>
        <RegistrationPromoCodeBanner
          promoModalProps={form.values._promoModalProps}
          isLoading={isValidating}
          codeError={codeError}
          referralCode={form.values._referralCodeValue}
          promoCode={form.values._promoCodeValue}
        />
        <SignUpContinueButton
          onClick={handleContinue}
          disabled={isButtonDisabled}
          data-test-id={dt.components.shared.auth.signUpVersionTwo.emailForm.continueButton}
          loading={isValidating}
        />
        <ConnectedRegistrationBonusBanner />
      </Stack>

      <Stack spacing={0}>
        <RecaptchaText textAlign="left" />
      </Stack>
    </>
  );
}
